var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { SecondaryGrid } from "@ui/components/grid";
import { GridFilters } from "@ui/components/grid/GridFilters";
import { SButton } from "@ui/components/buttons/SButton";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import { toast } from "@ui/utils/toaster";
import { apiErrorMessage, clientsApi } from "@api/enpoints/clients-api";
/** "min,max" from the number-range filter → the two optional bounds. */
const range = (value) => {
    const [min, max] = String(value !== null && value !== void 0 ? value : "").split(",");
    const n = (s) => (s !== undefined && s.trim() !== "" && !Number.isNaN(Number(s)) ? Number(s) : undefined);
    return [n(min), n(max)];
};
/** Clients with their location / AP / node counts; search, filters, sort and paging all run in the API. */
const ClientsPage = () => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ pageNumber: 1, pageSize: 25 });
    const [sortState, setSortState] = useState({ field: "name", order: "asc" });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const requestId = useRef(0);
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(handle);
    }, [search]);
    useEffect(() => {
        setPage((p) => (Object.assign(Object.assign({}, p), { pageNumber: 1 })));
    }, [debouncedSearch, appliedFilters, sortState]);
    const load = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const id = ++requestId.current;
        setLoading(true);
        const [locMin, locMax] = range(appliedFilters.locationCount);
        const [apMin, apMax] = range(appliedFilters.linkedApCount);
        const [nodeMin, nodeMax] = range(appliedFilters.linkedNodeCount);
        const query = {
            name: appliedFilters.name || undefined,
            globalSearch: debouncedSearch || undefined,
            locationCountMin: locMin,
            locationCountMax: locMax,
            linkedApCountMin: apMin,
            linkedApCountMax: apMax,
            linkedNodeCountMin: nodeMin,
            linkedNodeCountMax: nodeMax,
        };
        try {
            const response = yield clientsApi.getAll({
                filters: query,
                pageNumber: page.pageNumber,
                pageSize: page.pageSize,
                sortColumn: sortState === null || sortState === void 0 ? void 0 : sortState.field,
                sortDirection: sortState === null || sortState === void 0 ? void 0 : sortState.order,
            });
            if (id !== requestId.current)
                return;
            setRows((_b = (_a = response.data.data) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []);
            setTotal((_d = (_c = response.data.data) === null || _c === void 0 ? void 0 : _c.totalCount) !== null && _d !== void 0 ? _d : 0);
        }
        catch (error) {
            if (id === requestId.current)
                toast.error(apiErrorMessage(error, t("common:catalog.clients.loadFailed", "Could not load the clients.")));
        }
        finally {
            if (id === requestId.current)
                setLoading(false);
        }
    }), [appliedFilters, debouncedSearch, page, sortState, t]);
    useEffect(() => {
        load();
    }, [load]);
    const canEdit = hasPermission(Permission.CLIENTS_EDIT);
    const columns = [
        {
            field: "name",
            headerName: t("common:catalog.clients.client", "Client"),
            flex: 2,
            minWidth: 200,
            filterable: true,
            filterType: "text",
            sortable: true,
            renderCell: (params) => (React.createElement(Link, { component: "button", underline: "hover", color: "inherit", onClick: (e) => {
                    e.stopPropagation();
                    navigate(`/clients/${params.row.id}/detail`);
                } }, params.row.name)),
        },
        {
            field: "locationCount",
            headerName: t("common:catalog.clients.locations", "Locations"),
            flex: 1,
            minWidth: 110,
            filterable: true,
            filterType: "numberRange",
            sortable: true,
        },
        {
            field: "linkedApCount",
            headerName: t("common:catalog.clients.linkedAps", "Linked APs"),
            flex: 1,
            minWidth: 110,
            filterable: true,
            filterType: "numberRange",
            sortable: true,
        },
        {
            field: "linkedNodeCount",
            headerName: t("common:catalog.clients.linkedNodes", "Linked Nodes"),
            flex: 1,
            minWidth: 120,
            filterable: true,
            filterType: "numberRange",
            sortable: true,
        },
        ...(canEdit
            ? [
                {
                    field: "actions",
                    headerName: t("common:grid.actions", "Actions"),
                    minWidth: 120,
                    sortable: false,
                    filterable: false,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (React.createElement(SButton, { variant: "outlined", size: "small", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/clients/${params.row.id}`);
                        } }, t("common:actions.edit", "Edit"))),
                },
            ]
            : []),
    ];
    const handleSortModelChange = (model) => {
        var _a;
        const first = model[0];
        setSortState(first ? { field: first.field, order: ((_a = first.sort) !== null && _a !== void 0 ? _a : "asc") } : { field: "name", order: "asc" });
    };
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:catalog.clients.title", "Clients"), topActions: hasPermission(Permission.CLIENTS_CREATE) ? (React.createElement(SButton, { variant: "contained", size: "large", onClick: () => navigate("/clients/new") }, t("common:catalog.clients.add", "Add Client"))) : undefined }),
        React.createElement(PageContent, { sx: { pb: 0 } },
            React.createElement(GridFilters, { title: t("common:catalog.clients.count", { count: total, defaultValue: "{{count}} Clients" }), loading: loading, columns: columns.filter((c) => c.filterable), filters: filters, onChange: setFilters, searchProp: search, handleSearchChange: (e) => setSearch(e.target.value), sortState: sortState, onSortChange: (field, order) => setSortState({ field, order }), onApplyFilters: (applied) => setAppliedFilters(Object.assign({}, (applied !== null && applied !== void 0 ? applied : filters))), onClearFilters: () => {
                    setFilters({});
                    setAppliedFilters({});
                } }),
            React.createElement(SecondaryGrid, { rows: rows, columns: columns, loading: loading, getRowId: (row) => row.id, paginationMode: "server", sortingMode: "server", filterMode: "server", disableColumnFilter: true, sortModel: sortState ? [{ field: sortState.field, sort: sortState.order }] : [], onSortModelChange: handleSortModelChange, rowCount: total, paginationModel: { page: page.pageNumber - 1, pageSize: page.pageSize }, onPaginationModelChange: (model) => setPage({ pageNumber: model.page + 1, pageSize: model.pageSize }), pageSizeOptions: [5, 25, 50], renderCard: (row) => (React.createElement(Box, { sx: { p: 2, borderRadius: 3, bgcolor: "background.paper", mb: 1 }, onClick: () => navigate(`/clients/${row.id}/detail`) },
                    React.createElement(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 } },
                        React.createElement(Typography, { variant: "subtitle1" }, row.name),
                        canEdit && (React.createElement(SButton, { variant: "outlined", size: "small", onClick: (e) => {
                                e.stopPropagation();
                                navigate(`/clients/${row.id}`);
                            } }, t("common:actions.edit", "Edit")))),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 } },
                        t("common:catalog.clients.locations", "Locations"),
                        ": ",
                        row.locationCount,
                        " \u00B7 ",
                        t("common:catalog.clients.linkedAps", "Linked APs"),
                        ":",
                        " ",
                        row.linkedApCount,
                        " \u00B7 ",
                        t("common:catalog.clients.linkedNodes", "Linked Nodes"),
                        ": ",
                        row.linkedNodeCount))) }))));
};
export default ClientsPage;
